import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useBooking } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import {
  formatPrice,
  calculatePricingWithCredits,
  CreditAwarePricing,
  getNumericPrice,
  getNumericDuration,
  getTotalDuration,
  getTotalPrice
} from '../../utils/bookingUtils';
import { generateGuestPseudoEmail, isPseudoEmail, normalizePhoneForLookup } from '../../utils/phone';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';
import { format, parseISO, addMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Props for CreditOnlyPaymentForm (no Stripe needed)
interface CreditOnlyPaymentFormProps {
  pricing: CreditAwarePricing;
  onCreditOnlyPayment: () => Promise<void>;
  onBack: () => void;
}

// Separate component for credit-only payments (doesn't use Stripe hooks)
const CreditOnlyPaymentForm: React.FC<CreditOnlyPaymentFormProps> = ({ pricing, onCreditOnlyPayment, onBack }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      await onCreditOnlyPayment();
    } catch (err: any) {
      setErrorMessage(err.message || 'Credit payment failed');
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Confirm Your Booking</h2>
      <p className="text-gray-600 mb-6">Your member credits cover this booking!</p>

      {/* Payment summary */}
      <div className="bg-[#F1F7F4] p-4 rounded-md mb-6">
        <h3 className="font-medium text-[#232347] mb-3">Booking Summary</h3>

        <div className="flex justify-between text-gray-700">
          <span>Subtotal:</span>
          <span>${formatPrice(pricing.subtotal)}</span>
        </div>

        <div className="flex justify-between text-teal-600 mt-1">
          <span>Free Service Credits ({pricing.freeServicesCount} service{pricing.freeServicesCount !== 1 ? 's' : ''}):</span>
          <span>-${formatPrice(pricing.totalCreditSavings)}</span>
        </div>

        <div className="flex justify-between font-medium text-[#232347] mt-2 pt-2 border-t border-gray-200">
          <span>Total:</span>
          <span className="text-teal-600 font-bold">FREE</span>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
          {errorMessage}
        </div>
      )}

      <div className="bg-teal-50 p-4 rounded-md border border-teal-200 mb-6">
        <div className="flex items-center mb-2">
          <svg className="h-6 w-6 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium text-teal-800">Complimentary Booking</span>
        </div>
        <p className="text-teal-700">
          Your VIP member credits cover this entire booking. No payment required!
        </p>
        {pricing.creditsApplied.map(credit => (
          <p key={credit.serviceId} className="text-sm text-teal-600 mt-1">
            {credit.quantity}x {credit.serviceName} - FREE
          </p>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
        >
          Back
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isProcessing}
          className="px-6 py-2 rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Processing...
            </>
          ) : (
            'Confirm Free Booking'
          )}
        </button>
      </div>
    </div>
  );
};

// Props for PaymentFormInner
interface PaymentFormInnerProps {
  pricing: CreditAwarePricing;
  onWalletPayment: () => Promise<void>;
  onBack: () => void;
  onCreditOnlyPayment: () => Promise<void>;
  onPaymentSuccess: (isPayLater?: boolean, aptId?: string) => Promise<void>;
  appointmentId: string | null;  // Passed directly to avoid React state timing issues
}

// Inner component that has access to Stripe Elements
const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({ pricing, onWalletPayment, onBack, onCreditOnlyPayment, onPaymentSuccess, appointmentId }) => {
  const { bookingState, nextStep, setPaymentIntent, setPaymentStatus, saveBookingState } = useBooking();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'wallet' | 'credits'>('card');

  const stripe = useStripe();
  const elements = useElements();
  const totalAmount = pricing.finalAmount;
  const isCreditOnlyPayment = pricing.totalCreditSavings > 0 && pricing.finalAmount === 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Save booking state to localStorage before payment (for Afterpay redirects)
      saveBookingState();

      // Confirm payment with redirect support for Afterpay
      // Don't send pseudo-emails to Stripe - send phone instead
      const customerEmail = bookingState.customerInfo?.email;
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/booking?step=confirmation&appointment_id=${bookingState.appointmentId}`,
          payment_method_data: {
            billing_details: {
              name: `${bookingState.customerInfo?.firstName} ${bookingState.customerInfo?.lastName}`,
              email: isPseudoEmail(customerEmail) ? undefined : customerEmail,
              phone: bookingState.customerInfo?.phone,
            },
          },
        },
        redirect: 'if_required', // Only redirect for payment methods that require it (like Afterpay)
      });

      if (error) {
        // Payment failed or was cancelled
        setErrorMessage(error.message || 'Payment failed');
        setPaymentStatus('failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded without redirect (e.g., card payment)
        setPaymentStatus('succeeded');

        // Send confirmation email after successful card payment
        console.log('[DEBUG PaymentFormInner handleSubmit] appointmentId prop:', appointmentId);
        await onPaymentSuccess(false, appointmentId || undefined);

        // Move to confirmation step (webhook will confirm payment)
        nextStep();
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      setPaymentStatus('failed');
      setIsProcessing(false);
    }
  };

  // Handle wallet payment
  const handleWalletPayment = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      await onWalletPayment();
    } catch (err: any) {
      setErrorMessage(err.message || 'Wallet payment failed');
      setIsProcessing(false);
    }
  };

  // Handle credit-only payment (when total is $0 due to credits)
  const handleCreditOnlyPayment = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      await onCreditOnlyPayment();
    } catch (err: any) {
      setErrorMessage(err.message || 'Credit payment failed');
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Complete Payment</h2>
      <p className="text-gray-600 mb-6">Choose your payment method to complete your booking</p>

      {/* Payment summary with discount breakdown */}
      <div className="bg-[#F1F7F4] p-4 rounded-md mb-6">
        <h3 className="font-medium text-[#232347] mb-3">Payment Summary</h3>

        {/* Subtotal */}
        <div className="flex justify-between text-gray-700">
          <span>Subtotal:</span>
          <span>${formatPrice(pricing.subtotal)}</span>
        </div>

        {/* Credit Savings */}
        {pricing.totalCreditSavings > 0 && (
          <div className="flex justify-between text-teal-600 mt-1">
            <span>Free Service Credits ({pricing.freeServicesCount} service{pricing.freeServicesCount !== 1 ? 's' : ''}):</span>
            <span>-${formatPrice(pricing.totalCreditSavings)}</span>
          </div>
        )}

        {/* Member Discount (applied to paid services only) */}
        {pricing.memberDiscountAmount > 0 && (
          <div className="flex justify-between text-green-600 mt-1">
            <span>Member Discount ({pricing.memberDiscountPercent}%):</span>
            <span>-${formatPrice(pricing.memberDiscountAmount)}</span>
          </div>
        )}

        {/* Pay Now Discount */}
        {pricing.payNowDiscountAmount > 0 && (
          <div className="flex justify-between text-green-600 mt-1">
            <span>Pay Now Discount ({pricing.payNowDiscountPercent}%):</span>
            <span>-${formatPrice(pricing.payNowDiscountAmount)}</span>
          </div>
        )}

        {/* Total */}
        <div className="flex justify-between font-medium text-[#232347] mt-2 pt-2 border-t border-gray-200">
          <span>Total:</span>
          <span>${formatPrice(pricing.finalAmount)}</span>
        </div>

        {/* Wallet Balance */}
        {pricing.walletBalance > 0 && (
          <div className="flex justify-between text-blue-600 mt-2 pt-2 border-t border-gray-200">
            <span>Wallet Balance:</span>
            <span>${formatPrice(pricing.walletBalance)}</span>
          </div>
        )}
      </div>

      {/* Credit-Only Payment (when total is $0) */}
      {isCreditOnlyPayment && (
        <div className="space-y-6">
          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
              {errorMessage}
            </div>
          )}

          <div className="bg-teal-50 p-4 rounded-md border border-teal-200">
            <div className="flex items-center mb-2">
              <svg className="h-6 w-6 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-teal-800">Complimentary Booking</span>
            </div>
            <p className="text-teal-700">
              Your member credits cover this entire booking! No payment required.
            </p>
            {pricing.creditsApplied.map(credit => (
              <p key={credit.serviceId} className="text-sm text-teal-600 mt-1">
                {credit.quantity}x {credit.serviceName} - FREE
              </p>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleCreditOnlyPayment}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                'Confirm Free Booking'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Payment Method Selection (if wallet available and not credit-only) */}
      {!isCreditOnlyPayment && pricing.canPayWithWallet && (
        <div className="mb-6">
          <label className="block text-[#232347] mb-3 font-medium">Choose Payment Method</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setPaymentMethod('wallet')}
              className={`p-4 rounded-md border-2 text-left transition-colors ${
                paymentMethod === 'wallet'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-medium">Wallet</span>
              </div>
              <p className="text-sm text-gray-600">Pay with your ${formatPrice(pricing.walletBalance)} balance</p>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod('card')}
              className={`p-4 rounded-md border-2 text-left transition-colors ${
                paymentMethod === 'card'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-medium">Card / Afterpay</span>
              </div>
              <p className="text-sm text-gray-600">Pay with card or split payments</p>
            </button>
          </div>
        </div>
      )}

      {/* Card Payment Form */}
      {!isCreditOnlyPayment && (paymentMethod === 'card' || !pricing.canPayWithWallet) && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[#232347] mb-2">Payment Method</label>
            <div className="bg-white">
              <PaymentElement />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Your payment information is secure and encrypted.
            </p>
            <p className="text-sm text-[#3A5E63] mt-1">
              💳 Pay with card or split into 4 interest-free payments with Afterpay
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
              {errorMessage}
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
            >
              Back
            </button>

            <button
              type="submit"
              disabled={!stripe || !elements || isProcessing}
              className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                `Pay $${formatPrice(totalAmount)}`
              )}
            </button>
          </div>
        </form>
      )}

      {/* Wallet Payment Section */}
      {!isCreditOnlyPayment && paymentMethod === 'wallet' && pricing.canPayWithWallet && (
        <div className="space-y-6">
          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
              {errorMessage}
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-blue-800">Pay with Wallet</span>
            </div>
            <p className="text-blue-700">
              ${formatPrice(pricing.finalAmount)} will be deducted from your wallet balance.
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Remaining balance after payment: ${formatPrice(pricing.walletBalance - pricing.finalAmount)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleWalletPayment}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                `Pay $${formatPrice(pricing.finalAmount)} with Wallet`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Payment Choice UI (shows Pay Now vs Pay Later options for all users)
interface PaymentChoiceProps {
  pricingPayNow: CreditAwarePricing;
  pricingPayLater: CreditAwarePricing;
  onSelectPayNow: () => void;
  onSelectPayLater: () => void;
  onBack: () => void;
  isProcessing: boolean;
}

const PaymentChoiceUI: React.FC<PaymentChoiceProps> = ({
  pricingPayNow,
  pricingPayLater,
  onSelectPayNow,
  onSelectPayLater,
  onBack,
  isProcessing,
}) => {
  // Track which option is selected (without navigating)
  const [selectedOption, setSelectedOption] = useState<'pay_now' | 'pay_later' | null>(null);

  // Check if user gets pay-now discount (non-members only)
  const hasPayNowDiscount = pricingPayNow.payNowDiscountPercent > 0;

  // Handle confirm button click
  const handleConfirm = () => {
    if (selectedOption === 'pay_now') {
      onSelectPayNow();
    } else if (selectedOption === 'pay_later') {
      onSelectPayLater();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Payment</h2>
      <p className="text-gray-600 mb-6">Choose when you'd like to pay for your booking</p>

      <div className="space-y-4">
        {/* Pay Now Option */}
        <button
          type="button"
          onClick={() => setSelectedOption('pay_now')}
          disabled={isProcessing}
          className={`w-full p-5 rounded-lg border-2 text-left disabled:opacity-50 transition-colors relative ${
            selectedOption === 'pay_now'
              ? hasPayNowDiscount
                ? 'border-green-600 bg-green-100 ring-2 ring-green-600'
                : 'border-blue-600 bg-blue-100 ring-2 ring-blue-600'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          {/* Selection indicator */}
          {selectedOption === 'pay_now' && (
            <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${
              hasPayNowDiscount ? 'bg-green-600' : 'bg-blue-600'
            }`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <span className="text-2xl mr-3">{hasPayNowDiscount ? '✨' : '💳'}</span>
              <span className="text-lg font-semibold text-[#232347]">
                {hasPayNowDiscount
                  ? `Pay Now & Save ${pricingPayNow.payNowDiscountPercent}%`
                  : 'Pay Now'}
              </span>
            </div>
            {hasPayNowDiscount && (
              <span className={`bg-green-500 text-white text-sm px-3 py-1 rounded-full font-medium ${selectedOption === 'pay_now' ? 'mr-8' : ''}`}>
                Best Value
              </span>
            )}
          </div>

          <div className="ml-9 space-y-1">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal:</span>
              <span>${formatPrice(pricingPayNow.subtotal)}</span>
            </div>
            {/* Show member discount if applicable */}
            {pricingPayNow.memberDiscountAmount > 0 && (
              <div className="flex justify-between text-green-600 font-medium">
                <span>Member Discount ({pricingPayNow.memberDiscountPercent}%):</span>
                <span>-${formatPrice(pricingPayNow.memberDiscountAmount)}</span>
              </div>
            )}
            {/* Show pay-now discount for non-members */}
            {hasPayNowDiscount && (
              <div className="flex justify-between text-green-600 font-medium">
                <span>Pay Now Discount ({pricingPayNow.payNowDiscountPercent}%):</span>
                <span>-${formatPrice(pricingPayNow.payNowDiscountAmount)}</span>
              </div>
            )}
            <div className={`flex justify-between text-[#232347] font-bold text-lg pt-1 border-t ${
              hasPayNowDiscount ? 'border-green-200' : 'border-blue-200'
            }`}>
              <span>Total:</span>
              <span>${formatPrice(pricingPayNow.finalAmount)}</span>
            </div>
          </div>

          <p className="ml-9 mt-3 text-sm text-gray-600">
            Pay with Card, Afterpay, or Wallet
          </p>
        </button>

        {/* Pay Later Option */}
        <button
          type="button"
          onClick={() => setSelectedOption('pay_later')}
          disabled={isProcessing}
          className={`w-full p-5 rounded-lg border-2 text-left disabled:opacity-50 transition-colors relative ${
            selectedOption === 'pay_later'
              ? 'border-gray-600 bg-gray-100 ring-2 ring-gray-600'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          {/* Selection indicator */}
          {selectedOption === 'pay_later' && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-3">🏪</span>
            <span className="text-lg font-semibold text-[#232347]">Pay Later at Spa</span>
          </div>

          <div className="ml-9 space-y-1">
            <div className="flex justify-between text-[#232347] font-bold text-lg">
              <span>Total:</span>
              <span>${formatPrice(pricingPayLater.finalAmount)}</span>
            </div>
          </div>

          <p className="ml-9 mt-3 text-sm text-gray-600">
            Payment collected at your appointment
          </p>
        </button>
      </div>

      <div className="mt-8 flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isProcessing || !selectedOption}
          className="px-6 py-2 rounded-md bg-[#B2D8C5] text-[#232347] font-medium hover:bg-[#9BC9B4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

// Outer component that manages payment flow
const PaymentForm: React.FC = () => {
  const { bookingState, prevStep, setPaymentStatus, setPaymentIntent, nextStep, setAppointmentId } = useBooking();
  const { isMember, userProfile, refreshUserProfile, memberCredits, refreshMemberCredits } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentChoice, setPaymentChoice] = useState<'pay_now' | 'pay_later' | null>(null);
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const creationInitiatedRef = useRef(false);
  const appointmentCreatedRef = useRef(false);

  // Ref to store appointmentId synchronously (avoids React state timing issues)
  const appointmentIdRef = useRef<string | null>(null);

  // Calculate wallet balance
  const walletBalance = userProfile?.wallet_balance || 0;

  // Calculate pricing for both scenarios with credits applied
  const pricingPayNow = calculatePricingWithCredits(
    bookingState.selectedServices,
    isMember,
    walletBalance,
    memberCredits,
    bookingState.creditsToUse,
    undefined,
    true
  );
  const pricingPayLater = calculatePricingWithCredits(
    bookingState.selectedServices,
    isMember,
    walletBalance,
    memberCredits,
    bookingState.creditsToUse,
    undefined,
    false
  );

  // Check if this is a credit-only payment (total is $0)
  const isCreditOnlyPayment = pricingPayNow.totalCreditSavings > 0 && pricingPayNow.finalAmount === 0;

  // All users (members and non-members) can choose between Pay Now and Pay Later
  const shouldShowChoice = true;

  // Track if email was already sent (to prevent duplicates)
  const emailSentRef = useRef(false);

  /**
   * Gets existing client ID or creates a new client profile
   * Moved from BookingSummary to prevent duplicate appointments
   */
  const getOrCreateClientId = async (user: any): Promise<string> => {
    const { customerInfo } = bookingState;
    const isStaffBooking = userProfile?.role === 'admin' || userProfile?.role === 'crew';

    // Admin "book for user" — user already exists, use their ID directly
    if (bookingState.bookingForClientId) {
      return bookingState.bookingForClientId;
    }

    // Admin/crew booking on behalf of a guest — skip the logged-in user branch
    if (user && isStaffBooking) {
      // 1. Normalize phone for lookup
      const normalizedPhone = customerInfo?.phone
        ? normalizePhoneForLookup(customerInfo.phone)
        : null;

      // 2. Try to find existing profile by phone or email
      const { data: existingProfileId, error: lookupError } = await supabase
        .rpc('find_profile_by_phone_or_email', {
          p_phone: normalizedPhone || '',
          p_email: customerInfo?.email || '',
        });

      if (!lookupError && existingProfileId) {
        return existingProfileId;
      }

      // 3. No existing profile — create a guest profile
      let emailForProfile = customerInfo!.email;
      if (!emailForProfile || emailForProfile.trim() === '') {
        emailForProfile = generateGuestPseudoEmail(customerInfo!.phone);
      }

      const { data: guestProfileId, error: rpcError } = await supabase
        .rpc('get_or_create_guest_profile', {
          p_email: emailForProfile,
          p_full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
          p_preferred_name: customerInfo!.firstName,
          p_phone: customerInfo!.phone,
        });

      if (rpcError) {
        throw new Error(`Error creating guest profile: ${rpcError.message}`);
      }
      if (!guestProfileId) {
        throw new Error('Failed to get or create guest profile');
      }

      return guestProfileId;
    }

    // Regular authenticated user — use their own ID
    if (user) {
      const clientId = user.id;

      const { error: profileError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', clientId)
        .single();

      if (profileError) {
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: clientId,
            full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
            preferred_name: customerInfo!.firstName,
            email: customerInfo!.email,
            phone_number: customerInfo!.phone,
            account_status: 'active'
          });

        if (insertError) {
          throw new Error(`Error creating profile: ${insertError.message}`);
        }
      }

      return clientId;
    }

    // User not authenticated - use RPC function to get or create guest profile
    // Generate pseudo-email for guests without email (using phone number)
    let emailForProfile = customerInfo!.email;
    if (!emailForProfile || emailForProfile.trim() === '') {
      emailForProfile = generateGuestPseudoEmail(customerInfo!.phone);
    }

    const { data: profileId, error: rpcError } = await supabase
      .rpc('get_or_create_guest_profile', {
        p_email: emailForProfile,
        p_full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
        p_preferred_name: customerInfo!.firstName,
        p_phone: customerInfo!.phone
      });

    if (rpcError) {
      throw new Error(`Error creating guest profile: ${rpcError.message}`);
    }

    if (!profileId) {
      throw new Error('Failed to get or create guest profile');
    }

    return profileId;
  };

  /**
   * Creates the main appointment record
   */
  const createAppointment = async (clientId: string, startTime: string, endTime: string): Promise<string> => {
    const { selectedServices, selectedStaff, customerInfo } = bookingState;
    const isStaffBooking = userProfile?.role === 'admin' || userProfile?.role === 'crew';

    // Use RPC function to create appointment (bypasses RLS, matches mobile app pattern)
    const { data: appointmentId, error: appointmentError } = await supabase
      .rpc('create_guest_appointment', {
        p_client_id: clientId,
        p_service_id: selectedServices[0].id,
        p_start_time: startTime,
        p_end_time: endTime,
        p_staff_id: selectedStaff?.id || null,
        p_status: 'scheduled',
        p_payment_status: 'pending',
        p_source: isStaffBooking ? 'admin' : 'online',
        p_notes: customerInfo?.notes || null,
      });

    if (appointmentError) {
      if (appointmentError.message?.includes('TIME_SLOT_CONFLICT')) {
        throw new Error('This time slot was just booked by someone else. Please go back and select a different time.');
      }
      throw new Error(`Error creating appointment: ${appointmentError.message}`);
    }

    return appointmentId;
  };

  /**
   * Adds all selected services to the appointment_services junction table
   */
  const addServicesToAppointment = async (appointmentId: string): Promise<void> => {
    const { selectedServices, selectedStaff } = bookingState;

    // Use RPC function to insert services (bypasses RLS, matches mobile app pattern)
    const servicesData = selectedServices.map(service => ({
      service_id: service.id,
      price: getNumericPrice(service),
      duration: getNumericDuration(service),
      staff_id: selectedStaff?.id || null,
    }));

    const { error: servicesError } = await supabase
      .rpc('insert_appointment_services', {
        p_appointment_id: appointmentId,
        p_services: servicesData,
      });

    if (servicesError) {
      throw new Error(`Error adding services to appointment: ${servicesError.message}`);
    }
  };

  /**
   * Ensures an appointment is created before proceeding with payment.
   * Returns the appointment ID if successful.
   * This is called when user confirms payment (Pay Now, Pay Later, Wallet, or Credit-only).
   */
  const ensureAppointmentCreated = async (): Promise<string> => {
    // Return existing appointment ID if already created
    if (bookingState.appointmentId) {
      return bookingState.appointmentId;
    }

    // Validate required data
    const { selectedTime, customerInfo, selectedServices } = bookingState;
    if (!selectedTime || !customerInfo || selectedServices.length === 0) {
      throw new Error('Missing required booking information.');
    }

    // Get current authenticated user (if any)
    const { data: { user } } = await supabase.auth.getUser();

    // Calculate start and end times
    const utcDateTime = parseISO(selectedTime);
    const totalDuration = getTotalDuration(selectedServices);
    const startTimeISO = selectedTime.replace('Z', '');
    const endTimeISO = addMinutes(utcDateTime, totalDuration).toISOString().replace('Z', '');

    // Get or create client ID
    const clientId = await getOrCreateClientId(user);

    // Create the appointment with pending payment status
    const appointmentId = await createAppointment(clientId, startTimeISO, endTimeISO);

    // Add services to the appointment
    await addServicesToAppointment(appointmentId);

    // Store in ref FIRST (synchronous, immediately available)
    appointmentIdRef.current = appointmentId;

    // Also store in context (async, for other components)
    setAppointmentId(appointmentId);

    return appointmentId;
  };

  /**
   * Sends booking confirmation email to customer
   * Called after payment decision is made (Pay Now success, Pay Later, Wallet, or Credit-only)
   * @param isPayLater - Whether this is a pay later booking
   * @param aptId - Optional appointment ID to use (avoids React state timing issues)
   */
  const sendBookingConfirmationEmail = async (isPayLater: boolean = false, aptId?: string): Promise<void> => {
    // Prevent duplicate emails
    if (emailSentRef.current) {
      return;
    }

    const { customerInfo, selectedServices, selectedStaff, selectedTime, appointmentId: stateAppointmentId } = bookingState;

    // Use passed aptId, then ref, then state (in order of reliability)
    const appointmentId = aptId || appointmentIdRef.current || stateAppointmentId;

    if (!selectedTime || !appointmentId || !customerInfo) {
      console.error('Missing required data for confirmation email', { selectedTime, appointmentId, hasCustomerInfo: !!customerInfo });
      return;
    }

    // Generate pseudo-email for guests without email (for edge function to process)
    // The edge function will skip customer email but still send admin notifications
    const customerEmail = customerInfo.email && customerInfo.email.trim() !== ''
      ? customerInfo.email
      : generateGuestPseudoEmail(customerInfo.phone);

    emailSentRef.current = true;

    try {
      // Get session token for authorization (works for both authenticated and guest users)
      const { data: { session } } = await supabase.auth.getSession();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      };

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      // Generate receipt first (only for paid appointments, not Pay Later)
      // This is idempotent - returns existing receipt if already created by webhook
      if (!isPayLater) {
        try {
          await fetch(edgeFunctionUrl(EdgeFunctions.GENERATE_RECEIPT), {
            method: 'POST',
            headers,
            body: JSON.stringify({
              receipt_type: 'appointment',
              reference_id: appointmentId,
              user_id: session?.user?.id || null,
              payment_method: 'card', // Will be overwritten if receipt already exists
            }),
          });
          console.log('Receipt generated/verified for appointment:', appointmentId);
        } catch (receiptErr) {
          console.warn('Receipt generation skipped:', receiptErr);
          // Continue with email - receipt is optional
        }
      }

      const servicesText = selectedServices.map(s => s.name).join(', ');
      const totalPrice = getTotalPrice(selectedServices);

      // Parse time for formatting
      const utcDateTime = parseISO(selectedTime);
      const appointmentDateTime = toZonedTime(utcDateTime, 'America/Chicago');

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.SEND_BOOKING_CONFIRMATION), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointmentId,
          customerEmail, // Use generated pseudo-email if original was empty
          customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
          appointmentDate: format(appointmentDateTime, 'EEEE, MMMM d, yyyy'),
          appointmentTime: format(appointmentDateTime, 'h:mm a'),
          services: servicesText,
          staffName: selectedStaff?.name || null,
          totalPrice: formatPrice(totalPrice),
          isPayLater, // Pass to email function
          customerPhone: customerInfo.phone || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Email sending failed:', errorData);
      }
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      // Don't throw - email failure shouldn't block the booking
    }
  };

  // Create payment intent when user selects "Pay Now" (skip for credit-only payments)
  useEffect(() => {
    const createPaymentIntent = async () => {
      // Skip if already initiated, not pay_now, or credit-only payment
      if (creationInitiatedRef.current || paymentChoice !== 'pay_now' || isCreditOnlyPayment) {
        return;
      }
      creationInitiatedRef.current = true;

      try {
        setIsLoading(true);
        setIsCreatingAppointment(true);

        // Create appointment if not already created (point of no return for Pay Now)
        const aptId = await ensureAppointmentCreated();

        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        };

        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CREATE_PAYMENT_INTENT),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              amount: pricingPayNow.finalAmount,
              appointment_id: aptId,
              services: bookingState.selectedServices.map(service => ({
                id: service.id,
                name: service.name
              })),
              // Include discount info for tracking
              original_amount: pricingPayNow.subtotal,
              member_discount_amount: pricingPayNow.memberDiscountAmount,
              member_discount_percent: pricingPayNow.memberDiscountPercent,
              pay_now_discount_amount: pricingPayNow.payNowDiscountAmount,
              pay_now_discount_percent: pricingPayNow.payNowDiscountPercent,
              is_member: isMember,
              // Include credits to use (for webhook processing)
              credits_to_use: bookingState.creditsToUse.map(c => ({
                service_id: c.serviceId,
                quantity: c.quantity
              })),
              user_id: userProfile?.id,
              credit_savings: pricingPayNow.totalCreditSavings,
              // Email for Stripe automatic receipt (skip empty or pseudo-emails)
              receipt_email: bookingState.customerInfo?.email &&
                !isPseudoEmail(bookingState.customerInfo.email) &&
                bookingState.customerInfo.email.trim() !== ''
                  ? bookingState.customerInfo.email
                  : undefined,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error?.message || errorData.error || `Error ${response.status}`;
          } catch (e) {
            errorMessage = `Error ${response.status}: ${errorText}`;
          }
          throw new Error(errorMessage);
        }

        const { clientSecret } = await response.json();
        if (!clientSecret) {
          throw new Error('No client secret returned from server');
        }

        setClientSecret(clientSecret);
        setPaymentStatus('pending');
      } catch (error: any) {
        console.error('Error creating payment intent:', error);
        setError(error.message || 'Failed to initialize payment');
        creationInitiatedRef.current = false; // Allow retry on error
      } finally {
        setIsLoading(false);
        setIsCreatingAppointment(false);
      }
    };

    if (paymentChoice === 'pay_now' && !isCreditOnlyPayment) {
      createPaymentIntent();
    }
  }, [paymentChoice, isCreditOnlyPayment]);

  // Handle Pay Later selection
  const handlePayLater = async () => {
    setIsLoading(true);
    setIsCreatingAppointment(true);
    setError(null);

    try {
      // Create appointment if not already created (point of no return)
      const aptId = await ensureAppointmentCreated();

      // Clean up any stale pending card payment records (e.g. from user selecting Pay Now then going back)
      const { error: cleanupError } = await supabase
        .rpc('cleanup_pending_card_payments', { p_appointment_id: aptId });
      if (cleanupError) {
        console.warn('Failed to cleanup pending card payments:', cleanupError);
        // Non-blocking — continue with pay-later flow
      }

      const { error: updateError } = await supabase
        .from('appointments')
        .update({ payment_status: 'pending_payment' })
        .eq('id', aptId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Send confirmation email after payment decision is made (Pay Later = true)
      await sendBookingConfirmationEmail(true, aptId);

      setPaymentStatus('pending');
      setPaymentIntent('pay_later');
      nextStep();
    } catch (err: any) {
      console.error('Pay Later error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setIsCreatingAppointment(false);
    }
  };

  // Handle wallet payment
  const handleWalletPayment = async () => {
    if (!userProfile?.id) {
      throw new Error('Missing required information for wallet payment');
    }

    // Create appointment if not already created (point of no return)
    const aptId = await ensureAppointmentCreated();

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Call wallet payment edge function
    const response = await fetch(
      edgeFunctionUrl(EdgeFunctions.USE_WALLET_PAYMENT),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointment_id: aptId,
          amount: pricingPayNow.finalAmount,
          original_amount: pricingPayNow.subtotal,
          member_discount_amount: pricingPayNow.memberDiscountAmount,
          member_discount_percent: pricingPayNow.memberDiscountPercent,
          pay_now_discount_amount: pricingPayNow.payNowDiscountAmount,
          pay_now_discount_percent: pricingPayNow.payNowDiscountPercent,
          // Include credits to use
          credits_to_use: bookingState.creditsToUse.map(c => ({
            service_id: c.serviceId,
            quantity: c.quantity
          })),
          credit_savings: pricingPayNow.totalCreditSavings,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Wallet payment failed');
    }

    // Update appointment status
    await supabase
      .from('appointments')
      .update({ payment_status: 'paid' })
      .eq('id', aptId);

    // Refresh user profile and credits
    await refreshUserProfile();
    await refreshMemberCredits();

    // Send confirmation email after successful wallet payment
    await sendBookingConfirmationEmail(false, aptId);

    // Mark payment as successful
    setPaymentStatus('succeeded');
    setPaymentIntent('wallet_payment');
    nextStep();
  };

  // Handle credit-only payment (when total is $0 due to credits covering everything)
  const handleCreditOnlyPayment = async () => {
    if (!userProfile?.id) {
      throw new Error('Missing required information for credit payment');
    }

    // Create appointment if not already created (point of no return)
    const aptId = await ensureAppointmentCreated();

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Call wallet payment edge function with $0 amount (credits only)
    const response = await fetch(
      edgeFunctionUrl(EdgeFunctions.USE_WALLET_PAYMENT),
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointment_id: aptId,
          amount: 0,
          original_amount: pricingPayNow.subtotal,
          member_discount_amount: pricingPayNow.memberDiscountAmount,
          member_discount_percent: pricingPayNow.memberDiscountPercent,
          pay_now_discount_amount: 0,
          pay_now_discount_percent: 0,
          // Include credits to use
          credits_to_use: bookingState.creditsToUse.map(c => ({
            service_id: c.serviceId,
            quantity: c.quantity
          })),
          credit_savings: pricingPayNow.totalCreditSavings,
          is_credit_only: true,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Credit payment failed');
    }

    // Update appointment status
    await supabase
      .from('appointments')
      .update({ payment_status: 'paid' })
      .eq('id', aptId);

    // Refresh user profile and credits
    await refreshUserProfile();
    await refreshMemberCredits();

    // Send confirmation email after successful credit-only payment
    await sendBookingConfirmationEmail(false, aptId);

    // Mark payment as successful
    setPaymentStatus('succeeded');
    setPaymentIntent('credit_payment');
    nextStep();
  };

  // Handle back from payment form to choice selection
  const handleBackToChoice = () => {
    if (shouldShowChoice) {
      // Non-member: go back to choice selection
      setPaymentChoice(null);
      setClientSecret(null);
      creationInitiatedRef.current = false;
    } else {
      // Member: go back to previous step
      prevStep();
    }
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
        <button
          onClick={() => {
            setError(null);
            setPaymentChoice(null);
            setClientSecret(null);
            creationInitiatedRef.current = false;
          }}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Show payment choice for non-members
  if (shouldShowChoice && paymentChoice === null) {
    return (
      <PaymentChoiceUI
        pricingPayNow={pricingPayNow}
        pricingPayLater={pricingPayLater}
        onSelectPayNow={() => setPaymentChoice('pay_now')}
        onSelectPayLater={handlePayLater}
        onBack={prevStep}
        isProcessing={isLoading}
      />
    );
  }

  // Credit-only payment (no Stripe needed - use separate component)
  if (isCreditOnlyPayment && paymentChoice === 'pay_now') {
    return (
      <CreditOnlyPaymentForm
        pricing={pricingPayNow}
        onCreditOnlyPayment={handleCreditOnlyPayment}
        onBack={handleBackToChoice}
      />
    );
  }

  // Loading state (creating appointment and/or payment intent)
  if (isLoading || isCreatingAppointment || (paymentChoice === 'pay_now' && !clientSecret && !isCreditOnlyPayment)) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-12 w-12 border-4 border-[#B2D8C5] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">{isCreatingAppointment ? 'Creating your appointment...' : 'Initializing payment...'}</p>
      </div>
    );
  }

  // Show Stripe payment form (skip if credit-only payment)
  if (clientSecret && !isCreditOnlyPayment) {
    const options = {
      clientSecret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#B2D8C5',
        },
      },
    };

    return (
      <Elements stripe={stripePromise} options={options}>
        <PaymentFormInner
          pricing={pricingPayNow}
          onWalletPayment={handleWalletPayment}
          onBack={handleBackToChoice}
          onCreditOnlyPayment={handleCreditOnlyPayment}
          onPaymentSuccess={sendBookingConfirmationEmail}
          appointmentId={appointmentIdRef.current}
        />
      </Elements>
    );
  }

  return null;
};

export default PaymentForm;
